import { IOrder } from "modules/order/domain/models/IOrder";
import { PixApprovedTransaction } from "modules/payment/domain/models/PixApprovedTransaction";
import { GetError } from "utils/GetError";
import { PENDING_PIX_RETENTION_MS } from "./pendingPixPolicy";

export const PENDING_PIX_STORAGE_KEY = "@meep/pendingPix";

export type PendingPixStatus = "waiting" | "sending" | "sent" | "send_failed" | "expired";

export interface IPendingPix {
	pixId: string;
	kind: "Payment" | "Recharge";
	order: IOrder;
	value: number;
	payer?: { name?: string; ref?: string };
	status: PendingPixStatus;
	createdAt: number;
	checkAttempts: number;
	approvedAt?: number;
	pix?: PixApprovedTransaction;
	sendAttempts: number;
	lastError?: string;
	/** Erro da última consulta de status; some quando o servidor responde de verdade. */
	lastCheckError?: string;
	approvedBy?: "screen" | "background";
	nfceStatus?: "none" | "emitted" | "failed";
	/** A tela viu a aprovação mas o envio falhou: o verificador emite a NFC-e após reenviar. */
	nfcePending?: boolean;
	localId: string;
}

type Listener = (items: IPendingPix[]) => void;

const listeners = new Set<Listener>();
const activeIds = new Set<string>();

const sortRecentFirst = (items: IPendingPix[]) => [...items].sort((a, b) => b.createdAt - a.createdAt);

const read = (): IPendingPix[] => {
	try {
		const parsed = JSON.parse(localStorage.getItem(PENDING_PIX_STORAGE_KEY) ?? "[]");
		// Entradas corrompidas não podem derrubar a fila
		return Array.isArray(parsed)
			? parsed.filter((it: any) => it && typeof it === "object" && typeof it.pixId === "string")
			: [];
	} catch {
		return [];
	}
};

const write = (items: IPendingPix[]) => {
	try {
		localStorage.setItem(PENDING_PIX_STORAGE_KEY, JSON.stringify(items));
	} catch (error) {
		// storage indisponível/cheio: segue só com os assinantes
		GetError(error, "write", "pendingPixQueue", "Falha ao gravar a fila de PIX");
	}
	const sorted = sortRecentFirst(items);
	listeners.forEach((listener) => listener(sorted));
};

// Não guarda imagens base64 no localStorage
const stripQr = <T extends object | undefined>(value: T): T => {
	if (!value || !("qrCodeBase64" in value)) {
		return value;
	}
	const { qrCodeBase64, ...rest } = value as any;
	return rest as T;
};

const sanitize = (item: IPendingPix): IPendingPix => ({
	...item,
	order: item.order ? { ...item.order, paymentPix: stripQr((item.order as any).paymentPix) } : item.order,
	pix: stripQr(item.pix),
});

export const getPendingPixList = (): IPendingPix[] => sortRecentFirst(read());

export const upsertPendingPix = (item: IPendingPix): void => {
	const clean = sanitize(item);
	const items = read();
	const exists = items.some((it) => it.pixId === clean.pixId);
	write(exists ? items.map((it) => (it.pixId === clean.pixId ? clean : it)) : [...items, clean]);
};

export const updatePendingPix = (pixId: string, patch: Partial<IPendingPix>): IPendingPix | undefined => {
	const items = read();
	const current = items.find((it) => it.pixId === pixId);
	if (!current) {
		return undefined;
	}
	const updated = sanitize({ ...current, ...patch });
	write(items.map((it) => (it.pixId === pixId ? updated : it)));
	return updated;
};

export const prunePendingPix = (now: number): void => {
	const items = read();
	const kept = items.filter(
		(it) => !(["sent", "expired", "send_failed"].includes(it.status) && now - it.createdAt > PENDING_PIX_RETENTION_MS)
	);
	if (kept.length !== items.length) {
		write(kept);
	}
};

export const subscribePendingPix = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const markPixActive = (pixId: string): void => {
	activeIds.add(pixId);
};

export const releasePixActive = (pixId: string): void => {
	activeIds.delete(pixId);
};

export const isPixActive = (pixId: string): boolean => activeIds.has(pixId);
